
import { useState, useEffect } from 'react';
import { cleanUsageStatistics } from '@/lib/analytics';
import { useFetchStats } from './useFetchStats';
import { useAnalyticsErrors } from './useAnalyticsErrors';
import { toast } from 'sonner';
import { adminLog } from '@/lib/utils/admin-logger';
import { AuthFilter } from '@/components/analytics/AuthFilterButtons';

export const useAnalyticsCore = (
  selectedRange: number, 
  userId: string | undefined, 
  authFilter: AuthFilter = 'all'
) => {
  // Global loading state
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [totalEvents, setTotalEvents] = useState(0);

  // Initialize the stats fetching hook
  const {
    pageStats,
    actionStats,
    dailyStats,
    platformStats,
    browserStats,
    osStats,
    timeStats,
    languageStats,
    referrerStats,
    sessionMetrics,
    exitPages,
    utmStats,
    loadingStats: {
      loadingPageStats,
      loadingActionStats,
      loadingDailyStats,
      loadingPlatformStats,
      loadingBrowserStats,
      loadingOSStats,
      loadingTimeStats,
      loadingLanguageStats,
      loadingTotalStats,
      loadingReferrerStats,
      loadingSessionMetrics,
      loadingExitPages,
      loadingUTMStats,
    },
    fetchAllStats,
    resetLoadingStates
  } = useFetchStats(selectedRange, authFilter);

  // Initialize the error handling hook
  const {
    errors,
    hasErrors,
    pageStatsError,
    actionStatsError,
    dailyStatsError,
    platformStatsError,
    browserStatsError,
    osStatsError,
    timeStatsError,
    languageStatsError,
    totalStatsError,
    referrerStatsError,
    sessionMetricsError,
    exitPagesError,
    utmStatsError,
    setPageStatsError,
    setActionStatsError,
    setDailyStatsError,
    setPlatformStatsError,
    setBrowserStatsError,
    setOSStatsError,
    setTimeStatsError,
    setLanguageStatsError,
    setTotalStatsError,
    setReferrerStatsError,
    setSessionMetricsError,
    setExitPagesError,
    setUTMStatsError,
    resetAllErrors
  } = useAnalyticsErrors();

  // Function to load all data
  const loadData = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      adminLog(`Loading analytics for ${selectedRange} day range with auth filter: ${authFilter}`);
      
      // Set end date to end of current day to include all of today's data
      const endDate = new Date();
      endDate.setHours(23, 59, 59, 999);
      
      // Set start date to beginning of the day X days ago
      const startDate = new Date(endDate);
      startDate.setDate(endDate.getDate() - selectedRange + 1); // +1 to include today
      startDate.setHours(0, 0, 0, 0);

      adminLog(`Date range: ${startDate.toISOString()} to ${endDate.toISOString()}`);

      // Fetch all stats (total will be calculated from daily stats)
      await fetchAllStats(
        startDate, 
        endDate, 
        {
          onPageStatsError: setPageStatsError,
          onActionStatsError: setActionStatsError,
          onDailyStatsError: setDailyStatsError,
          onPlatformStatsError: setPlatformStatsError,
          onBrowserStatsError: setBrowserStatsError,
          onOSStatsError: setOSStatsError,
          onTimeStatsError: setTimeStatsError,
          onLanguageStatsError: setLanguageStatsError,
          onReferrerStatsError: setReferrerStatsError,
          onSessionMetricsError: setSessionMetricsError,
          onExitPagesError: setExitPagesError,
          onUTMStatsError: setUTMStatsError,
        }
      );
      
      setLoading(false);
    } catch (error) {
      console.error('Error in fetchAllStats:', error);
      toast.error('Some analytics data could not be loaded');
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedRange, userId, authFilter]);

  // Calculate total events from daily stats (eliminates separate database query)
  useEffect(() => {
    const total = dailyStats.reduce((sum, item) => sum + item.count, 0);
    setTotalEvents(total);
    adminLog(`Total events calculated from daily stats: ${total}`);
  }, [dailyStats]);

  // Function to trigger data cleanup with specified timeframe
  const cleanupOldData = async (timeframe: string = 'halfYear'): Promise<number> => {
    try {
      const deletedCount = await cleanUsageStatistics(timeframe);
      
      // If records were deleted, refresh the data
      if (deletedCount > 0) {
        // Reset states before fetching fresh data
        resetLoadingStates();
        resetAllErrors();
        
        // Reload data to reflect changes
        await loadData();
      }
      
      return deletedCount;
    } catch (error) {
      console.error('Error cleaning up data:', error);
      toast.error('Failed to clean up data');
      throw error;
    }
  };

  // Retry function to reload all data
  const retryAllData = () => {
    resetLoadingStates();
    resetAllErrors();
    setLoading(true);
    loadData();
  };

  return {
    // Data
    pageStats,
    actionStats,
    dailyStats,
    platformStats,
    browserStats,
    osStats,
    timeStats,
    languageStats,
    referrerStats,
    sessionMetrics,
    exitPages,
    utmStats,
    totalEvents,
    
    // Global loading state
    loading,
    
    // Individual loading states
    loadingPageStats,
    loadingActionStats,
    loadingDailyStats,
    loadingPlatformStats,
    loadingBrowserStats,
    loadingOSStats,
    loadingTimeStats,
    loadingLanguageStats,
    loadingTotalStats,
    loadingReferrerStats,
    loadingSessionMetrics,
    loadingExitPages,
    loadingUTMStats,
    
    // Errors
    hasErrors,
    errors,
    
    // Functions
    cleanupOldData,
    retryAllData,
    loadData
  };
};
